// Tools for use in the interactive console

import * as config from './config';
import * as dm from './domain';
import * as utils from './utils';
import Application from './Application';
import DefaultsManager from './DefaultsManager';
import DomainModel from './DomainModel';
import RenderPipeline from './RenderPipeline';
import WallView from './views/WallView';
import animateOpen from './utils/animateOpen';
import lookAtWall from './utils/lookAtWall';
import toTechnicalDocument from './utils/toTechnicalDocument';
import traverse from './utils/traverseGenerator';
import { CameraHelper, DirectionalLight, DirectionalLightHelper, Mesh, MeshStandardMaterial, Object3D, LineSegments, LineBasicMaterial, WireframeGeometry, Matrix4, BufferGeometry, PerspectiveCamera } from 'three';
import { EdgeList, SweptMesh, ThreeJSMesh } from './mw/prims';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { elevation } from './measurement';
import { sortedByConnection } from './controllers/wallControllers';

const ln = (str: string, indent: number) => {
    return '\t'.repeat(indent) + str + '\n';
}

export const toGlbFile = (obj: Object3D): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        let exporter = new GLTFExporter();
        exporter.parse(
            obj,
            (glb: ArrayBuffer) => resolve(glb),
            error => reject(error),
            { binary: true }
        );
    });
}

const array = (property: string, model: any, indent: number) => {
    let result = '';
    if(model[property] && model[property].length > 0) {
        result += ln(`${property} (${model[property].length})`, indent);
        for(let child of model[property]) {
            result += describe(child, indent+1);
        }
    }
    return result;
}

const vecln = (property: string, model: any, indent: number) => {
    let result = '';
    const {x, y, z} = model[property] as {x: number, y: number, z: number};
    result += ln(`${property}: ${x.toFixed(3)}, ${y.toFixed(3)}, ${z.toFixed(3)}`, indent);
    return result;
}

export const describe = (model: any, indent: number = 0): string => {
    let result = "";
    switch(model.constructor) {
        case dm.ClosetUnit:
            result += ln('ClosetUnit', indent++);
            result += describe(model.content, indent+1);
            break;
        case dm.Section:
            result += ln('Section', indent++);
            result += ln(`width: ${model.width}`, indent);
            result += ln(`height: ${model.height}`, indent);
            result += ln(`elevation: ${elevation(model)}`, indent);
            result += array('sections', model, indent);
            result += array('parametricParts', model, indent);
            result += array('drawers', model, indent);
            result += array('assets', model, indent);
           
            if(model.door) {
                result += describe(model.door, indent);
            }
            break;
        case dm.ParametricPart:
            result += ln(`ParametricPart (${model.name})`, indent);
            break;
        case dm.Drawer:
            result += ln(`Drawer (${model.height}")`, indent);
            break;
        case dm.GenericAsset:
            result += ln('GenericAsset', indent++);
            result += ln(`filename: ${model.filename}`, indent);
            result += ln(`height: ${model.height}`, indent);
            result += ln(`position.y: ${model.position.y}`, indent);
            break;
        case dm.Door:
            result += ln(`Door (${model.hinge})`, indent++);
            result += ln(`width: ${model.width}`, indent);
            result += array('handles', model, indent);
            break;
        case dm.Handle:
            result += ln(`Handle`, indent++);
            result += ln(`side: ${model.side}`, indent);
            result += vecln('position', model, indent);
    }
    return result;
}

(window as any).dev = {

    tree() {
        const app: any = (window as any).app;
        console.log(describe(app.model));
    },

    // store a design in local storage under the given name
    store(name: string) {
        if(!name) {
            console.error(`Can't store with name "${name}"`);
            return;
        }
        const app: any = (window as any).app;
        const str = localStorage.getItem('CPS_unit_dev_stored_units');
        if(str) {
            const data = JSON.parse(str);
            data[name] = app.model.toJSON();
            localStorage.setItem('CPS_unit_dev_stored_units',
                                 JSON.stringify(data));
        } else {
            localStorage.setItem('CPS_unit_dev_stored_units',
                                 JSON.stringify({
                                     [name]: app.model.toJSON()
                                 }));
        }
    },

    // lists available stored designs
    stored() {
        const str = localStorage.getItem('CPS_unit_dev_stored_units');
        if(str) {
            const data = JSON.parse(str);
            return Array.from(Object.keys(data));
        } else {
            return [];
        }
    },

    load(name: string) {
        const app: any = (window as any).app;
        const str = localStorage.getItem('CPS_unit_dev_stored_units');
        if(str) {
            const data = JSON.parse(str);
            if(name in data) {
                app.model = dm.ClosetUnit.fromJSON(data[name]);
                app.refresh();
            } else {
                console.error(`No stored unit named "${name}"`);
            }
        } else {
            console.error('No stored units');
        }
    },

    edgeBanding() {
        const app: any = (window as any).app;
        app.model.traverse((model: DomainModel) => {
            if(model instanceof dm.Divider || model instanceof dm.ClosetUnit) {
                model.materials[0] = dm.Material.fromJSON({
                    "type": "StandardMelamine",
                    "color": `${config.colors}/358.png`,
                });
            } else if(model instanceof dm.ParametricPart
                      && model.name == 'AdjustableShelf')
            {
                model.materials[0] = dm.Material.fromJSON({
                    "type": "StandardMelamine",
                    "color": `${config.colors}/358.png`,
                });
            }
        });
        app.commit(null, 'Update divider materials');
        app.refresh();
    },

    updateLoopRunning: false,

    startUpdateLoop() {
        const app: any = (window as any).app;
        this.updateLoopRunning = true;
        const animate = () => {
            if(this.updateLoopRunning) {
                requestAnimationFrame(animate);
                app.pipeline.update = true;
                console.log('update');
            }
        }
        animate();
    },

    stopUpdateLoop() {
        this.updateLoopRunning = false;
    },

    async reset(url: string) {
        const
            app: Application = (window as any).app,
            response = await fetch(url),
            data = await response.json(),
            config = dm.ClosetConfig.fromJSON(data);

        app.defaults = new DefaultsManager(config.defaults);

        app.model.traverse((model: DomainModel) => {
            app.defaults.reset(model);
        });
        app.refresh();
    },

    wireframe(object: Object3D = null) {
        const app: Application = (window as any).app;
        utils.wireframe(object == null ? app.object : object);
        app.pipeline.update = true;
    },

    animateOpen(amount: number, model: DomainModel) {
        if('open' in model) {
            animateOpen(amount, model as any);
        }
    },

    techDoc(obj: Object3D, doorOpacity: number = 1) {
        toTechnicalDocument(obj, false, doorOpacity);
    },

    async export(obj: Object3D) {
        const buffer = await toGlbFile(obj);
        utils.saveBuffer(buffer, 'export.glb');
    },

    directionalLightHelper(root: Object3D) {
        for(const obj of traverse(root)) {
            if(obj instanceof DirectionalLight) {
                const helper = new DirectionalLightHelper(obj);
                root.add(helper);

                if(obj.castShadow) {
                    const camera = obj.shadow.camera;
                    const helper = new CameraHelper(camera);
                    root.add(helper);
                }
            }
        }
    },

    swept() {
        const app: Application = (window as any).app;

        const profile = new EdgeList();
        profile.move(1, 0);
        profile.move(0, 1);
        profile.arc(-Math.PI/2, Math.PI, 1, 6);
        profile.move(0, 1);
        profile.move(-1, 0);
//        profile.move(0, 1);
//        profile.move(3, 3);
//        profile.move(0, 1);
//        profile.move(-3, 3);
//        profile.move(-1, 0);

        app.pipeline.scene.add(profile.toLineSegments());

        const plan = new EdgeList();
        plan.move(0, 6);
        plan.move(-12, 0);
        plan.move(0, -6);
        plan.move(-12, 0);
        plan.move(0, 6);
        plan.move(-12, 0);
        plan.move(0, -6);

        const planVis = plan.toLineSegments();
        planVis.rotation.x = Math.PI/2;
        app.pipeline.scene.add(planVis);

        const sw = new SweptMesh(profile, plan);
        const geo = ThreeJSMesh.toBufferGeometry(sw.generate());

        const mesh = new Mesh(
            //geo, new MeshStandardMaterial({transparent: true, opacity: 0.6}));
            geo, new MeshStandardMaterial());
        const wireframe = new LineSegments(
            new WireframeGeometry(geo),
            new LineBasicMaterial(
                {color: 0x808080, transparent: true, opacity: 0.6}));

        app.pipeline.scene.add(mesh);
        //app.pipeline.scene.add(wireframe);
        //app.pipeline.scene.add(new VertexNormalsHelper(mesh));
        //app.pipeline.scene.add(new utils.ThreeAxisHelper());
    },

    profile() {
        const app: Application = (window as any).app;

        const profile = new EdgeList();
        profile.move(0, 12);
        profile.move(3, 0);
        profile.move(0, 3);
        profile.move(-3, 0);
        profile.move(0, 0);

        // We want to shear 45 degrees to the right of vertical
        const theta = -Math.PI/4;

        const m1 = new Matrix4();
        const m2 = new Matrix4();
        const s = new Matrix4();
        m1.makeShear(0, Math.tan(-theta), 0, 0, 0, 0);

        s.makeTranslation(0, 0, 6);
        m2.multiply(s);
        s.makeRotationY(Math.PI/4);
        m2.multiply(s);
        const group = profile.toLineSegments();
        for(let child of group.children) {
            console.log(child);
            if((child as any).geometry instanceof BufferGeometry) {
                const geo = (child as Mesh).geometry
                geo.applyMatrix4(m1);
                geo.applyMatrix4(m2);
                geo.computeBoundingBox();
            }
        }

        app.pipeline.scene.add(group);
        app.pipeline.scene.add(profile.toLineSegments());
        app.pipeline.scene.add(new utils.ThreeAxisHelper());
    },

    freeview() {
        const app = (window as any).app;
        Application.do('view-3d');
        const tool = app.multitool;
        if(tool) {
            const orbit = tool.orbit;
            orbit.controls.enablePan = true;
            orbit.controls.screenSpacePanning = true;
            orbit.controls.minAzimuthAngle = -Infinity;
            orbit.controls.maxAzimuthAngle = Infinity;
            orbit.controls.minDistance = 0;
            orbit.controls.maxDistance = Infinity;
        }
    },

    sortWallsByConnection(walls: dm.Wall[]) {
        utils.shuffle(walls);
        return sortedByConnection(walls)
    },

    setupModel(overrides: any) {
        const
            app = (window as any).app,
            data = {
                layout: "U-Shape",
                island: false,
                style: "PE",
                pull: "pull1",
                wall1: 150,
                wall2: 192,
                wall3: 150,
            },
            selectionString = localStorage.getItem('kitchen-tool-selections'),
            selection = JSON.parse(selectionString);

        Object.assign(data, selection);
        Object.assign(data, overrides);
        localStorage.setItem('kitchen-tool-selections', JSON.stringify(data));

        app.common.setupModel();
        app.refresh();
    },

    lookAtWall(wall: WallView, pipeline: RenderPipeline) {
        if(! (pipeline.camera instanceof PerspectiveCamera)) return;
        const
            aspect = pipeline.canvas.width / pipeline.canvas.height,
            camera = pipeline.camera;
        lookAtWall(camera, wall, aspect);
        pipeline.update = true;
    },

    dump(obj: Object3D, indent: number = 0) {
        const leader = "\t".repeat(indent);
        console.log(`${leader}${obj.constructor.name} "${obj.name ?? ''}"`);
        for(const child of obj.children) {
            this.dump(child, indent+1);
        }
    }
}
